import { Flex, Stack, StackProps, Text } from '@mantine/core'

import {
  formatChangePercent,
  getPercentChangeColor,
} from '@/app/[locale]/price/[slug]/_components/CoinLineChart/utils'
import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import useCoinStore from '@/app/[locale]/price/[slug]/_store'
import { useCurrencyAmounts } from '@/hooks/business/useCurrencyAmounts'

interface FontSizeConfig {
  title: number
  price: number
  change: number
  timeRange: number
}

interface TokenPriceShareInfoProps {
  fontSizeConfig?: Partial<FontSizeConfig>
  change: number
  timeRange: string
}

const defaultSizeConfig: FontSizeConfig = {
  title: 45,
  price: 74,
  change: 48,
  timeRange: 40,
}

export default function TokenPriceShareInfo({
  fontSizeConfig: _fontSizeConfig,
  change,
  timeRange,
  ...stackProps
}: TokenPriceShareInfoProps & StackProps) {
  const fontSizeConfig: FontSizeConfig = {
    ...defaultSizeConfig,
    ..._fontSizeConfig,
  }
  const { coin } = useCoinContext()
  const livePrice = useCoinStore(s => s.liveCoinMarket.usd_price)
  const price = livePrice || coin.usd_price
  const {
    amounts: [{ formattedValue }],
  } = useCurrencyAmounts({
    amounts: [
      {
        value: price,
        currencyFormatOptions: { showCode: true, showSymbol: false },
      },
    ],
    tokenSymbol: coin.symbol,
    isUsedForTokenPrice: true,
    isExchangeRateBasedOnSymbolCurrency: false,
  })
  const changeColor = getPercentChangeColor(change)
  const changeText = formatChangePercent(change * 100)
  return (
    <Stack align={'center'} gap={0} {...stackProps}>
      <Text mt={60} style={{ fontSize: fontSizeConfig.title }}>
        {coin.name} ({coin.symbol})
      </Text>
      <Text style={{ fontSize: fontSizeConfig.price }}>{formattedValue}</Text>
      <Flex align={'center'}>
        <Text c={changeColor} style={{ fontSize: fontSizeConfig.change }}>
          {changeText}
        </Text>
        <Text ml={6} c={'var(--content-secondary)'} style={{ fontSize: fontSizeConfig.timeRange }}>
          ({timeRange})
        </Text>
      </Flex>
    </Stack>
  )
}
